// Geometry model + (de)serialization for editable line-family elements.
//
// A "line-editable" element is either a straight <line> (as drawn by the line
// tool) or a <path> that this tool produced (flagged with el.lineEdit). Both
// are described by the same structured model so the LineEditOverlay can:
//   • drag each anchor (node),
//   • fold a segment at its midpoint (double-click) — repeatable for multi-fold,
//   • bend any single segment into a smooth arc by dragging its midpoint handle
//     (with a dashed angle reference line), and straighten it again.
//
// The path we emit is restricted to M / L / Q commands so the existing
// translate / resize / export pipelines (which operate on attrs.d) keep working
// and the model round-trips losslessly through moves and scales.

const round = (n) => Math.round(n * 100) / 100;

export function isLineEditable(el) {
  if (!el) return false;
  if (el.type === "line") return true;
  if (el.type === "path" && (el.lineEdit || el.attrs?.lineEdit)) return true;
  return false;
}

// Whether an element carries the arrow flag (arrowhead drawn as path segments).
export function isArrow(el) {
  const v = el?.attrs?.arrow;
  return v === true || v === "true";
}

// Compare a path `d` string's suffix against a decoration command string,
// using numeric tolerance (0.5px) instead of exact string matching. This is
// necessary because endpointPath() recomputes decoration coordinates from
// geometry, and floating-point rounding can cause tiny differences (e.g.
// 465.64 vs 465.65) that break exact string matching.
//
// Both `d` and `deco` are path command strings (e.g. "L 465.64 206.63 M ...").
// Returns the length of the matched suffix in `d` (in characters), or 0 if no
// match. The matched suffix starts at d.length - returnedLength.
function pathEndsWithDeco(d, deco) {
  if (!deco) return 0;
  const dTokens = d.match(/[MLQAQZ][^MLQAQZ]*/gi) || [];
  const decoTokens = deco.match(/[MLQAQZ][^MLQAQZ]*/gi) || [];
  if (decoTokens.length === 0 || decoTokens.length > dTokens.length) return 0;
  const tol = 0.5;
  for (let i = 0; i < decoTokens.length; i++) {
    const dTok = dTokens[dTokens.length - decoTokens.length + i];
    const decoTok = decoTokens[i];
    if (dTok[0].toUpperCase() !== decoTok[0].toUpperCase()) return 0;
    const dNums = (dTok.slice(1).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
    const decoNums = (decoTok.slice(1).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
    if (dNums.length !== decoNums.length) return 0;
    for (let j = 0; j < dNums.length; j++) {
      if (Math.abs(dNums[j] - decoNums[j]) > tol) return 0;
    }
  }
  // Compute the character length of the matched suffix in d.
  // Rebuild the suffix from the matched d tokens to find its length.
  let suffixLen = 0;
  for (let i = dTokens.length - decoTokens.length; i < dTokens.length; i++) {
    suffixLen += dTokens[i].length;
  }
  // Account for whitespace between tokens (d may have spaces between tokens).
  // Find the actual suffix string by searching from the end.
  // Reconstruct what the suffix should look like and find it.
  // Simpler: join the matched tokens and find their total length in d.
  // Since tokens include leading command letter + args, and d may have
  // varying whitespace, we measure the suffix by finding the start position
  // of the first matched token in d.
  const firstTokStr = dTokens[dTokens.length - decoTokens.length];
  // Find the last occurrence of this token in d (it should be near the end).
  // But tokens can repeat, so we search from the position where the
  // non-suffix part ends. The non-suffix part is d minus the suffix.
  // We know the suffix has decoTokens.length tokens. The non-suffix part has
  // (dTokens.length - decoTokens.length) tokens. We find where the last
  // (dTokens.length - decoTokens.length) tokens end in d.
  let pos = 0;
  for (let i = 0; i < dTokens.length - decoTokens.length; i++) {
    pos = d.indexOf(dTokens[i], pos) + dTokens[i].length;
  }
  return d.length - pos;
}

// All supported endpoint decoration types. Both the start and the end of an
// arrow can use any of these. "none" means no decoration (plain stroke end).
export const ENDPOINT_TYPES = [
  "none",
  "round", // small filled circle (dot) at the endpoint
  "square", // short perpendicular bar across the endpoint
  "line-arrow", // ">" shape from two stroked line segments (default arrowhead)
  "triangle-arrow", // filled triangle pointing along the stroke
  "reversed-triangle", // filled triangle pointing backward (against stroke)
  "circle-arrow", // filled circle at the tip + ">" line arrow
  "diamond-arrow", // filled diamond at the tip
];

export const DEFAULT_START_TYPE = "none";
export const DEFAULT_END_TYPE = "line-arrow";

// Filled (solid) decoration types: their path is a closed shape that should
// be rendered with fill = stroke color. "line-arrow" and "square" are open
// stroked shapes and stay unfilled.
export const FILLED_DECO_TYPES = new Set([
  "round",
  "triangle-arrow",
  "reversed-triangle",
  "circle-arrow",
  "diamond-arrow",
]);
export function isFilledDeco(type) {
  return FILLED_DECO_TYPES.has(type);
}

// Read the start/end endpoint type from an element's attrs (with defaults).
export function startTypeOf(el) {
  return el?.attrs?.arrowStart || DEFAULT_START_TYPE;
}
export function endTypeOf(el) {
  return el?.attrs?.arrowEnd || DEFAULT_END_TYPE;
}

// Build the path-segment commands that decorate ONE endpoint of the stroke.
// `nodes` is the full node list; `sw` is the stroke width; `type` is one of
// ENDPOINT_TYPES; `isStart` selects the start (true) or end (false) endpoint.
// Returns a string of L/Q/Z commands (NO leading M) to append directly after
// the last node of the main stroke, so the decoration is part of the SAME
// sub-path as the main line. This is critical for stroke-dasharray draw
// animations: a single continuous sub-path ensures the dash pattern reveals
// the decoration progressively as the stroke reaches the endpoint, with no
// visual separation. When the decoration needs a "pen lift" (e.g. the second
// barb of a line-arrow), we use M to start a new sub-path — but the FIRST
// segment always continues from the main stroke via L.
// Build the decoration geometry for one endpoint, in ABSOLUTE coordinates.
// `nodes` are the main-stroke nodes (on-curve points only; curve control
// points are NOT included). `tangent` is an optional unit vector {x, y}
// pointing outward at the endpoint — when provided it overrides the
// neighbor-based direction so curved strokes get the correct tangent at the
// endpoint (the neighbor would otherwise give the chord direction, which is
// wrong for curves). `endpointTangentOf` (below) computes this from the raw
// path tokens.
export function endpointPath(nodes, sw = 3, type = "none", isStart = false, tangent = null) {
  if (!type || type === "none" || nodes.length < 2) return "";
  // Pick the two nodes that define the direction at this endpoint.
  // For the end: last two nodes (prev -> tip). For the start: first two nodes
  // (tip -> next), but the direction is reversed so the decoration points
  // outward from the start.
  const tip = isStart ? nodes[0] : nodes[nodes.length - 1];
  const neighbor = isStart ? nodes[1] : nodes[nodes.length - 2];
  // Direction from neighbor toward tip (points outward at the endpoint).
  // For curved strokes the caller passes the true endpoint tangent so the
  // decoration aligns with the curve's angle at the tip, not the chord.
  let dx, dy;
  if (tangent) {
    dx = tangent.x;
    dy = tangent.y;
  } else {
    dx = tip.x - neighbor.x;
    dy = tip.y - neighbor.y;
  }
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len; // unit vector pointing outward (along stroke at end)
  const uy = dy / len;
  const px = -uy; // perpendicular
  const py = ux;
  const headLen = Math.max(12, sw * 4);
  const headWid = Math.max(9, sw * 3);
  const dotR = Math.max(4, sw * 1.4);
  const barLen = Math.max(8, sw * 3);
  const r = round;

  switch (type) {
    case "round": {
      // Filled circle (dot) centered on the endpoint. Drawn as a closed
      // sub-path starting from the right edge of the circle.
      return ` L ${r(tip.x + dotR)} ${r(tip.y)} A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(tip.x - dotR)} ${r(tip.y)} A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(tip.x + dotR)} ${r(tip.y)} Z`;
    }
    case "square": {
      // Perpendicular bar across the endpoint. Goes from one side to the
      // other, forming a T shape with the main stroke.
      const b1x = tip.x + px * barLen;
      const b1y = tip.y + py * barLen;
      const b2x = tip.x - px * barLen;
      const b2y = tip.y - py * barLen;
      return ` L ${r(b1x)} ${r(b1y)} L ${r(b2x)} ${r(b2y)}`;
    }
    case "line-arrow": {
      // ">" shape: the first barb continues from the tip via L (same
      // sub-path as the main stroke), then M back to tip for the second barb.
      // This keeps the first barb in the main sub-path so the draw animation
      // reveals it progressively.
      const baseX = tip.x - ux * headLen;
      const baseY = tip.y - uy * headLen;
      const b1x = baseX + px * headWid;
      const b1y = baseY + py * headWid;
      const b2x = baseX - px * headWid;
      const b2y = baseY - py * headWid;
      return ` L ${r(b1x)} ${r(b1y)} M ${r(tip.x)} ${r(tip.y)} L ${r(b2x)} ${r(b2y)}`;
    }
    case "triangle-arrow": {
      // Filled triangle pointing outward (along the stroke direction).
      // First barb continues from tip via L, then to second barb, then close.
      const baseX = tip.x - ux * headLen;
      const baseY = tip.y - uy * headLen;
      const b1x = baseX + px * headWid;
      const b1y = baseY + py * headWid;
      const b2x = baseX - px * headWid;
      const b2y = baseY - py * headWid;
      return ` L ${r(b1x)} ${r(b1y)} L ${r(b2x)} ${r(b2y)} Z`;
    }
    case "reversed-triangle": {
      // Filled triangle pointing inward (against the stroke direction).
      // The base sits at the tip, the apex points back along the stroke.
      const apexX = tip.x - ux * headLen;
      const apexY = tip.y - uy * headLen;
      const b1x = tip.x + px * headWid;
      const b1y = tip.y + py * headWid;
      const b2x = tip.x - px * headWid;
      const b2y = tip.y - py * headWid;
      return ` L ${r(b1x)} ${r(b1y)} L ${r(apexX)} ${r(apexY)} L ${r(b2x)} ${r(b2y)} Z`;
    }
    case "circle-arrow": {
      // Filled circle centered ON the tip. This ensures the circle fully
      // covers the stroke's tip — including the round line-cap which extends
      // sw/2 past the endpoint — so the stroke never pokes out.
      const cx = tip.x;
      const cy = tip.y;
      return ` L ${r(cx + dotR)} ${r(cy)} A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(cx - dotR)} ${r(cy)} A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(cx + dotR)} ${r(cy)} Z`;
    }
    case "diamond-arrow": {
      // Filled diamond at the tip: four points forming a diamond shape.
      const midX = tip.x - ux * headLen * 0.6;
      const midY = tip.y - uy * headLen * 0.6;
      const backX = tip.x - ux * headLen * 1.2;
      const backY = tip.y - uy * headLen * 1.2;
      const b1x = midX + px * headWid;
      const b1y = midY + py * headWid;
      const b2x = midX - px * headWid;
      const b2y = midY - py * headWid;
      return ` L ${r(b1x)} ${r(b1y)} L ${r(backX)} ${r(backY)} L ${r(b2x)} ${r(b2y)} Z`;
    }
    default:
      return "";
  }
}

// Build a decoration path centered at the ORIGIN (0,0), with the tip at
// (0,0). This is the offset-path-friendly version of endpointPath(): when a
// path element has `offset-path` set, the browser places the element's
// coordinate-system origin (0,0) at the current point along the offset path.
// So a decoration drawn around (0,0) will travel along the main stroke with
// its tip exactly on the line's growing end.
//
// `nodes` and `sw` are the same as endpointPath(). `type` and `isStart` select
// the endpoint. Returns a FULL path string starting with "M 0 0" followed by
// the decoration geometry (all coordinates relative to the tip at origin).
export function endpointPathRelative(nodes, sw = 3, type = "none", isStart = false) {
  if (!type || type === "none" || nodes.length < 2) return "";
  // Use a FIXED reference direction (horizontal, pointing right for end / left
  // for start) instead of the actual stroke direction. The geometry is built
  // around this fixed axis, and `offset-rotate: auto` (set in animation.js)
  // rotates the whole decoration to match the path's tangent at the current
  // point. This way the decoration always aligns with the stroke's angle —
  // including on curved paths where the tangent changes along the way.
  const ux = isStart ? -1 : 1;
  const uy = 0;
  const px = -uy; // perpendicular (= 0)
  const py = ux;  // perpendicular (= 1 or -1)
  const headLen = Math.max(12, sw * 4);
  const headWid = Math.max(9, sw * 3);
  const dotR = Math.max(4, sw * 1.4);
  const barLen = Math.max(8, sw * 3);
  const r = round;
  // All geometry is computed relative to the tip, which is now (0,0).
  // So we simply drop the tip.x/tip.y offsets from every coordinate.
  switch (type) {
    case "round": {
      return `M ${r(dotR)} 0 A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(-dotR)} 0 A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(dotR)} 0 Z`;
    }
    case "square": {
      const b1x = px * barLen;
      const b1y = py * barLen;
      const b2x = -px * barLen;
      const b2y = -py * barLen;
      return `M 0 0 L ${r(b1x)} ${r(b1y)} L ${r(b2x)} ${r(b2y)}`;
    }
    case "line-arrow": {
      const baseX = -ux * headLen;
      const baseY = -uy * headLen;
      const b1x = baseX + px * headWid;
      const b1y = baseY + py * headWid;
      const b2x = baseX - px * headWid;
      const b2y = baseY - py * headWid;
      return `M 0 0 L ${r(b1x)} ${r(b1y)} M 0 0 L ${r(b2x)} ${r(b2y)}`;
    }
    case "triangle-arrow": {
      const baseX = -ux * headLen;
      const baseY = -uy * headLen;
      const b1x = baseX + px * headWid;
      const b1y = baseY + py * headWid;
      const b2x = baseX - px * headWid;
      const b2y = baseY - py * headWid;
      return `M 0 0 L ${r(b1x)} ${r(b1y)} L ${r(b2x)} ${r(b2y)} Z`;
    }
    case "reversed-triangle": {
      const apexX = -ux * headLen;
      const apexY = -uy * headLen;
      const b1x = px * headWid;
      const b1y = py * headWid;
      const b2x = -px * headWid;
      const b2y = -py * headWid;
      return `M 0 0 L ${r(b1x)} ${r(b1y)} L ${r(apexX)} ${r(apexY)} L ${r(b2x)} ${r(b2y)} Z`;
    }
    case "circle-arrow": {
      // Filled circle centered ON the endpoint (0,0). This ensures the circle
      // fully covers the stroke's tip — including the round line-cap which
      // extends sw/2 past the endpoint — so the stroke never pokes out.
      const cx = 0;
      const cy = 0;
      return `M ${r(cx + dotR)} ${r(cy)} A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(cx - dotR)} ${r(cy)} A ${r(dotR)} ${r(dotR)} 0 1 0 ${r(cx + dotR)} ${r(cy)} Z`;
    }
    case "diamond-arrow": {
      const midX = -ux * headLen * 0.6;
      const midY = -uy * headLen * 0.6;
      const backX = -ux * headLen * 1.2;
      const backY = -uy * headLen * 1.2;
      const b1x = midX + px * headWid;
      const b1y = midY + py * headWid;
      const b2x = midX - px * headWid;
      const b2y = midY - py * headWid;
      return `M 0 0 L ${r(b1x)} ${r(b1y)} L ${r(backX)} ${r(backY)} L ${r(b2x)} ${r(b2y)} Z`;
    }
    default:
      return "";
  }
}

// Backward-compatible wrapper: the classic ">" arrowhead at the path end.
export function arrowHeadPath(nodes, sw = 3) {
  return endpointPath(nodes, sw, "line-arrow", false);
}

// Compute the TRUE tangent direction (unit vector, pointing outward from the
// endpoint) at each end of the main stroke, by parsing the raw `mainD` tokens.
//
// For straight segments (L) the tangent is simply (tip - neighbor).
// For quadratic Bézier (Q) the tangent at the ENDPOINT is (end - control), and
// at the START is (control - start). Using the chord (end - start) here would
// be wrong for curves — the decoration would point along the chord instead of
// along the curve's actual direction at the tip, which is exactly the bug
// where adjusting the arc curvature doesn't update the decoration angle.
//
// Returns { startTangent: {x,y}|null, endTangent: {x,y}|null }.
function endpointTangentsOf(mainD) {
  const tokens = mainD.match(/[MLQAQZ][^MLQAQZ]*/gi) || [];
  if (tokens.length < 2) return { startTangent: null, endTangent: null };

  const segs = []; // each: { cmd, start:{x,y}, ctrl:{x,y}|null, end:{x,y} }
  let cur = null;
  for (const t of tokens) {
    const cmd = t[0].toUpperCase();
    const nums = (t.slice(1).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
    if (cmd === "M") {
      cur = { x: nums[0] || 0, y: nums[1] || 0 };
    } else if (cmd === "L") {
      const end = { x: nums[0] || 0, y: nums[1] || 0 };
      segs.push({ cmd: "L", start: cur, ctrl: null, end });
      cur = end;
    } else if (cmd === "Q") {
      const ctrl = { x: nums[0] || 0, y: nums[1] || 0 };
      const end = { x: nums[2] || 0, y: nums[3] || 0 };
      segs.push({ cmd: "Q", start: cur, ctrl, end });
      cur = end;
    } else if (cmd === "Z") {
      // close — treat as line back to first M; ignore for tangent purposes
    }
  }
  if (segs.length === 0) return { startTangent: null, endTangent: null };

  const unit = (x, y) => {
    const l = Math.hypot(x, y) || 1;
    return { x: x / l, y: y / l };
  };

  // Start tangent: direction leaving the start point (outward).
  const s0 = segs[0];
  let startTangent = null;
  if (s0.cmd === "Q" && s0.ctrl) {
    // outward at start = (control - start)
    startTangent = unit(s0.ctrl.x - s0.start.x, s0.ctrl.y - s0.start.y);
  } else {
    // L: outward = (end - start)
    startTangent = unit(s0.end.x - s0.start.x, s0.end.y - s0.start.y);
  }

  // End tangent: direction arriving at the end point (outward = tip - prev).
  const sn = segs[segs.length - 1];
  let endTangent = null;
  if (sn.cmd === "Q" && sn.ctrl) {
    // outward at end = (end - control)
    endTangent = unit(sn.end.x - sn.ctrl.x, sn.end.y - sn.ctrl.y);
  } else {
    // L: outward = (end - start)
    endTangent = unit(sn.end.x - sn.start.x, sn.end.y - sn.start.y);
  }

  return { startTangent, endTangent };
}

// Split an arrow element's `d` into two independent paths for animation:
//   • mainD  — the main stroke (M ... L/Q ...), with NO decorations.
//   • decoD  — the endpoint decorations as a STANDALONE path (starts with M),
//              so it can be rendered as a separate <path> that is NOT affected
//              by stroke-dasharray / stroke-dashoffset draw animations.
//
// This lets the arrowhead stay fully visible from the start of a draw
// animation while only the main line's length animates — the arrowhead's job
// is to indicate direction, so it should exist immediately.
//
// Returns null if the element is not an arrow or has no decorations.
// Split an arrow element's `d` into THREE independent paths:
//   • mainD       — the main stroke (M ... L/Q ...), with NO decorations.
//   • startDecoD  — the start endpoint decoration as a STANDALONE path (starts
//                   with M), or "" if no start decoration.
//   • endDecoD    — the end endpoint decoration as a STANDALONE path (starts
//                   with M), or "" if no end decoration.
//
// The three parts are fully independent — no shared sub-path, no connecting
// line — to avoid fill-rule bleeding or stroke-dasharray glitches that occur
// when decorations share the same sub-path as the main line.
//
// During draw animations, only `mainD` gets stroke-dasharray/dashoffset; the
// decorations are always fully visible from the start (their job is to
// indicate direction, so they should exist immediately). No offset-path
// animation is needed — the decorations stay fixed at their endpoints while
// the main line grows.
//
// Returns null if the element is not an arrow or has no decorations.
export function splitArrowPath(el) {
  if (!isArrow(el)) return null;
  const a = el.attrs || {};
  const sw = Number(a.strokeWidth) || 3;
  const sType = startTypeOf(el);
  const eType = endTypeOf(el);
  if (sType === "none" && eType === "none") return null;

  // Parse the full d to get all nodes (main + decoration points).
  let d = (a.d || "").replace(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi, (m) => round(Number(m)));
  const stripped = stripDecorations(d, el, sw, sType, eType);
  if (!stripped) return null;
  const { mainNodes, decoLen } = stripped;
  if (!mainNodes || mainNodes.length < 2 || decoLen <= 0) return null;

  // The main stroke d is everything before the decoration suffix.
  const mainD = d.slice(0, d.length - decoLen).trim();

  // Build standalone decoration paths.
  //
  // START decoration: stays fixed at the start endpoint (no animation). Uses
  // absolute coordinates with "M <startTip>" so it renders in place.
  //
  // END decoration: returns TWO forms:
  //   • endDecoD          — absolute coordinates ("M <endTip> ..."), used for
  //                         STATIC rendering (no animation). Renders in place
  //                         at the endpoint.
  //   • endDecoDRelative  — relative coordinates centered at origin (0,0),
  //                         used when a draw animation applies offset-path to
  //                         make the decoration travel along the main stroke.
  //                         The browser places the element's (0,0) at the
  //                         current point on the offset-path.
  const startTip = mainNodes[0];
  const endTip = mainNodes[mainNodes.length - 1];
  // Compute the TRUE endpoint tangents from the raw mainD so that on curved
  // strokes the decorations align with the curve's actual direction at the
  // tip (not the chord direction). This is what makes the decoration angle
  // update live when the user adjusts the arc curvature.
  const { startTangent, endTangent } = endpointTangentsOf(mainD);
  const startDecoRaw = endpointPath(mainNodes, sw, sType, true, startTangent);
  const endDecoRaw = endpointPath(mainNodes, sw, eType, false, endTangent);
  const startDecoDRelative = endpointPathRelative(mainNodes, sw, sType, true);
  const endDecoDRelative = endpointPathRelative(mainNodes, sw, eType, false);
  let startDecoD = "";
  let endDecoD = "";
  if (startDecoRaw) {
    startDecoD = `M ${round(startTip.x)} ${round(startTip.y)} ${startDecoRaw.trim()}`;
  }
  if (endDecoRaw) {
    endDecoD = `M ${round(endTip.x)} ${round(endTip.y)} ${endDecoRaw.trim()}`;
  }

  return { mainD, startDecoD, endDecoD, endDecoDRelative, startDecoDRelative, sType, eType };
}

// Strip endpoint decorations from a path `d` and return the main-stroke nodes.
// Tries the current sType/eType first, then falls back to trying ALL endpoint
// type combinations (to handle stale decorations from a just-changed type).
// When multiple type combinations match, picks the one with the LONGEST
// decoration suffix — this prevents false positives where a complex decoration
// (e.g. circle-arrow = circle + line-arrow barbs) is partially matched by a
// simpler type (e.g. line-arrow matches just the barbs at the end).
function stripDecorations(d, el, sw, sType, eType) {
  const allTokens = d.match(/[MLQAQZ][^MLQAQZ]*/gi) || [];
  const allNodes = [];
  for (const t of allTokens) {
    const cmd = t[0].toUpperCase();
    if (cmd === "M" || cmd === "L") {
      const nums = (t.slice(1).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
      allNodes.push({ x: nums[0] || 0, y: nums[1] || 0 });
    } else if (cmd === "Q") {
      const nums = (t.slice(1).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
      allNodes.push({ x: nums[2] || 0, y: nums[3] || 0 });
    }
  }
  if (allNodes.length < 2) return null;

  // For each candidate main-node count, find the BEST matching decoration
  // (longest suffix match). We try from the fewest main nodes (n=2) upward,
  // because fewer main nodes = longer decoration suffix = more complete strip.
  // The first n (from 2 up) that yields any match is returned, with the
  // longest decoration length found at that n.
  for (let n = 2; n <= allNodes.length - 1; n++) {
    const candidate = allNodes.slice(0, n);
    let bestLen = 0;
    // Try all type combinations, keep the longest match.
    for (const sTry of ENDPOINT_TYPES) {
      for (const eTry of ENDPOINT_TYPES) {
        const sd = endpointPath(candidate, sw, sTry, true);
        const ed = endpointPath(candidate, sw, eTry, false);
        // Try full (start + end) decoration
        const fd = sd + ed;
        if (fd) {
          const len = pathEndsWithDeco(d, fd);
          if (len > bestLen) bestLen = len;
        }
        // Try end-only (when start is none/empty)
        if (!sd || sTry === "none") {
          const len = pathEndsWithDeco(d, ed);
          if (len > bestLen) bestLen = len;
        }
      }
    }
    if (bestLen > 0) return { mainNodes: candidate, decoLen: bestLen };
  }
  return null;
}

export function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

// model = { nodes: [{x,y}...], curves: [null | {hx,hy} ...] }
// curves.length === nodes.length - 1. A null curve is a straight segment; a
// {hx,hy} curve is an arc whose on-curve apex (the draggable handle) sits at
// (hx,hy) — the quadratic passes through it at t = 0.5.
export function parseLineModel(el) {
  const a = el.attrs || {};
  if (el.type === "line") {
    const nodes = [
      { x: +a.x1 || 0, y: +a.y1 || 0 },
      { x: +a.x2 || 0, y: +a.y2 || 0 },
    ];
    return { nodes, curves: [null] };
  }
  let d = a.d || "";
  // Normalize all numeric values in d to 2 decimal places (matching round()),
  // so that string-suffix matching against regenerated decorations works even
  // when d contains high-precision floats (e.g. from drag/resize transforms).
  d = d.replace(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi, (m) => round(Number(m)));
  // Arrow elements embed endpoint decorations (start + end) as extra path
  // commands AFTER the main stroke. To strip them, we re-generate the exact
  // decoration strings from the element's arrowStart/arrowEnd types and remove
  // them from the end of `d`. This is more reliable than pattern-matching M
  // commands, because some decorations (e.g. line-arrow's first barb) continue
  // the main sub-path via L rather than starting a new sub-path via M.
  if (isArrow(el)) {
    const sw = Number(a.strokeWidth) || 3;
    // We need the main-stroke nodes to regenerate decorations. But we don't
    // have them yet (that's what we're parsing). Solution: parse the raw d
    // WITHOUT stripping first to get ALL nodes, then figure out which are
    // main-stroke vs decoration by re-generating and matching.
    //
    // We reuse stripDecorations() which tries ALL endpoint type combinations
    // and picks the longest suffix match. This handles both the normal case
    // (current types match) and the type-switch case (d has stale decorations
    // from a previous type, e.g. line-arrow → round).
    const curSType = startTypeOf(el);
    const curEType = endTypeOf(el);
    const stripped = stripDecorations(d, el, sw, curSType, curEType);
    if (stripped) {
      d = d.slice(0, d.length - stripped.decoLen).trim();
    }
  }
  const tokens = d.match(/[MLQ][^MLQ]*/gi) || [];
  const nodes = [];
  const curves = [];
  for (const t of tokens) {
    const cmd = t[0].toUpperCase();
    const nums = (t.slice(1).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
    if (cmd === "M") {
      nodes.push({ x: nums[0] || 0, y: nums[1] || 0 });
    } else if (cmd === "L") {
      nodes.push({ x: nums[0] || 0, y: nums[1] || 0 });
      curves.push(null);
    } else if (cmd === "Q") {
      const c = { x: nums[0] || 0, y: nums[1] || 0 };
      const p = { x: nums[2] || 0, y: nums[3] || 0 };
      const prev = nodes[nodes.length - 1];
      // On-curve apex at t=0.5: (P0 + 2C + P2) / 4.
      curves.push({ x_unused: 0, hx: (prev.x + 2 * c.x + p.x) / 4, hy: (prev.y + 2 * c.y + p.y) / 4 });
      nodes.push(p);
    }
  }
  if (nodes.length < 2) {
    const nodesFb = [{ x: 0, y: 0 }, { x: 1, y: 1 }];
    return { nodes: nodesFb, curves: [null] };
  }
  return { nodes, curves };
}

// Style attributes carried across line <-> path conversions (everything except
// the geometry keys, so stroke / fill / opacity / rotation / dash all persist).
function styleAttrs(attrs = {}) {
  const { x1, y1, x2, y2, points, d, ...rest } = attrs;
  return rest;
}

export function serializeLineModel(model, baseEl) {
  const { nodes, curves } = model;
  const base = styleAttrs(baseEl.attrs);
  const arrow = isArrow(baseEl);
  const sw = Number(baseEl.attrs?.strokeWidth) || 3;
  // Arrow elements are always emitted as <path> so the arrowhead segments
  // can be appended to `d` (a <line> has no `d`). Non-arrow straight lines
  // with exactly two nodes stay as the compact <line> form.
  const straightSingle = !arrow && nodes.length === 2 && !curves[0];
  if (straightSingle) {
    return {
      type: "line",
      attrs: {
        ...base,
        x1: round(nodes[0].x),
        y1: round(nodes[0].y),
        x2: round(nodes[1].x),
        y2: round(nodes[1].y),
      },
    };
  }
  let d = `M ${round(nodes[0].x)} ${round(nodes[0].y)}`;
  for (let i = 0; i < curves.length; i++) {
    const prev = nodes[i];
    const node = nodes[i + 1];
    const cv = curves[i];
    if (!cv) {
      d += ` L ${round(node.x)} ${round(node.y)}`;
    } else {
      // Recover the quadratic control point from the on-curve apex:
      // C = 2H - (P0 + P2) / 2.
      const cx = 2 * cv.hx - (prev.x + node.x) / 2;
      const cy = 2 * cv.hy - (prev.y + node.y) / 2;
      d += ` Q ${round(cx)} ${round(cy)} ${round(node.x)} ${round(node.y)}`;
    }
  }
  // Append endpoint decorations (start + end) after the main stroke. The first
  // segment of each decoration continues the main sub-path via L (no M), so
  // stroke-dasharray / stroke-dashoffset draw animations reveal the decoration
  // progressively as part of the same continuous stroke — no visual separation
  // between the main line and the arrowhead. Subsequent segments within a
  // decoration (e.g. the second barb of a line-arrow) use M as needed.
  // parseLineModel strips these decorations by regenerating and matching them.
  if (arrow) {
    const sType = startTypeOf(baseEl);
    const eType = endTypeOf(baseEl);
    d += endpointPath(nodes, sw, sType, true);
    d += endpointPath(nodes, sw, eType, false);
  }
  return {
    type: "path",
    attrs: { ...base, d, fill: base.fill ?? "none", lineEdit: true },
  };
}

// Handle position for a segment: arc apex when curved, chord midpoint when
// straight.
export function segHandle(model, i) {
  const cv = model.curves[i];
  if (cv) return { x: cv.hx, y: cv.hy };
  return midpoint(model.nodes[i], model.nodes[i + 1]);
}

function clone(model) {
  return {
    nodes: model.nodes.map((n) => ({ ...n })),
    curves: model.curves.map((c) => (c ? { ...c } : null)),
  };
}

export function moveNode(model, i, point) {
  const m = clone(model);
  m.nodes[i] = { x: point.x, y: point.y };
  return m;
}

// Set/clear a segment's arc. If the handle is dragged back near the chord
// midpoint it snaps straight again.
export function setSegCurve(model, i, handlePoint, straightThreshold = 6) {
  const m = clone(model);
  const mid = midpoint(m.nodes[i], m.nodes[i + 1]);
  const dx = handlePoint.x - mid.x;
  const dy = handlePoint.y - mid.y;
  if (Math.hypot(dx, dy) < straightThreshold) m.curves[i] = null;
  else m.curves[i] = { hx: handlePoint.x, hy: handlePoint.y };
  return m;
}

function distToSegment(p, a, b) {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const wx = p.x - a.x;
  const wy = p.y - a.y;
  const len2 = vx * vx + vy * vy || 1e-6;
  let t = (wx * vx + wy * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * vx;
  const cy = a.y + t * vy;
  return Math.hypot(p.x - cx, p.y - cy);
}

// Insert a new editable node on the segment nearest `point`, placed exactly on
// the line (the segment midpoint, or the arc's on-curve apex for a curved
// segment) — WITHOUT bending it. The line keeps its current shape; the user
// gets an extra control point they can then drag. Repeated calls add more
// nodes (turning the line into a multi-segment polyline).
export function foldNearest(model, point, offset = 0) {
  const m = clone(model);
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < m.curves.length; i++) {
    const dpt = distToSegment(point, m.nodes[i], m.nodes[i + 1]);
    if (dpt < bestDist) {
      bestDist = dpt;
      best = i;
    }
  }
  const a = m.nodes[best];
  const b = m.nodes[best + 1];
  const cv = m.curves[best];
  if (!cv) {
    // Straight segment: insert the chord midpoint, both halves stay straight.
    m.nodes.splice(best + 1, 0, midpoint(a, b));
    m.curves.splice(best, 1, null, null);
    return m;
  }
  // Curved (quadratic) segment: subdivide at t=0.5 via de Casteljau so the two
  // halves trace EXACTLY the same arc — the shape is preserved, we only add a
  // draggable node where the curve already passes.
  // Recover the control point from the on-curve apex, then split.
  const C = { x: 2 * cv.hx - (a.x + b.x) / 2, y: 2 * cv.hy - (a.y + b.y) / 2 };
  const X = { x: cv.hx, y: cv.hy }; // on-curve point at t=0.5
  const C1 = { x: (a.x + C.x) / 2, y: (a.y + C.y) / 2 };
  const C2 = { x: (C.x + b.x) / 2, y: (C.y + b.y) / 2 };
  // Apex (t=0.5 on-curve point) of each half-quadratic = (P0 + 2*Ctrl + P2)/4.
  const H1 = { hx: (a.x + 2 * C1.x + X.x) / 4, hy: (a.y + 2 * C1.y + X.y) / 4 };
  const H2 = { hx: (X.x + 2 * C2.x + b.x) / 4, hy: (X.y + 2 * C2.y + b.y) / 4 };
  m.nodes.splice(best + 1, 0, X);
  m.curves.splice(best, 1, H1, H2);
  return m;
}
